import { useCallback } from 'react';

import { FontAwesomeIcon, Input } from '@appcharge/shared-ui';
import { Portal } from 'common/contracts';

import { ESavingForm } from '../DeveloperSettings.types';
import { getNavigationPath } from '../helpers/developerSettings.helpers';
import { usePortalInfoForm } from '../hooks/usePortalInfoForm';

import { SettingsCard } from './SettingsCard';

interface IPortalInfoSectionProps {
  portalData: Portal | undefined;
  portalId: string | undefined;
  savingForm: ESavingForm;
  setSavingForm: (form: ESavingForm) => void;
  isAdmin: boolean;
  onCopy: (value: string, label: string) => void;
}

export const PortalInfoSection: React.FC<IPortalInfoSectionProps> = ({
  portalData,
  portalId,
  savingForm,
  setSavingForm,
  isAdmin,
  onCopy
}) => {
  const form = usePortalInfoForm({
    portalData,
    portalId,
    setSavingForm
  });

  const handleOpenDomain = useCallback((): void => {
    if (!form.values.domain) {
      return;
    }
    const navigationPath = getNavigationPath(form.values.domain);
    window.open(navigationPath, '_blank');
  }, [form.values.domain]);

  const handleCopyPortalId = useCallback((): void => {
    onCopy(portalData?.portalId || '', 'Portal ID');
  }, [onCopy, portalData?.portalId]);

  return (
    <SettingsCard
      title="Portal Info"
      description="Your portal project details."
      onSave={form.submitForm}
      isSaveDisabled={
        !form.isValid ||
        !form.dirty ||
        savingForm !== ESavingForm.NONE
      }
      isLoading={savingForm === ESavingForm.PORTAL_INFO}
      saveButtonDataTestId="portal-info-save"
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Portal project name
          </label>
          <Input
            name="portalName"
            value={form.values.portalName}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            placeholder="Portal project name"
            state={
              form.touched.portalName &&
              form.errors.portalName
                ? 'error'
                : 'default'
            }
            description={
              form.touched.portalName
                ? (form.errors.portalName as string)
                : undefined
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Portal ID
          </label>
          <Input
            value={portalData?.portalId || ''}
            disabled
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-copy"
                onClick={handleCopyPortalId}
              />
            }
            rightIconStyles="cursor-pointer text-base-mutedForeground"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Domain</label>
          <Input
            name="domain"
            value={form.values.domain}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled={!isAdmin}
            state={
              form.touched.domain && form.errors.domain
                ? 'error'
                : 'default'
            }
            description={
              form.touched.domain
                ? (form.errors.domain as string)
                : undefined
            }
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-arrow-up-right-from-square"
                onClick={handleOpenDomain}
              />
            }
            rightIconStyles="cursor-pointer text-base-mutedForeground"
          />
        </div>
      </div>
    </SettingsCard>
  );
};
